import type { AnalyticsHosts } from '../api';
import type { HostSelection } from '../filters';
import type { QualityExposure, QualityHostKind, QualityHostRow } from './types';

const MAX_HOSTS = 50;
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const PORT = /^[0-9]{1,5}$/;

/**
 * A host as the address keeps it: trimmed, lower case, without a port or one leading `www.`; null when it is
 * malformed (a scheme, path, query, fragment, control character, empty label or invalid hostname).
 */
export const normalizeHost = (raw: string): string | null => {
  let host = raw.replace(/^[\t\n\f\r ]+|[\t\n\f\r ]+$/g, '').toLowerCase();
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f]/.test(host)) return null;
  const colon = host.lastIndexOf(':');
  if (colon >= 0) {
    const port = host.slice(colon + 1);
    if (!PORT.test(port) || Number(port) > 65535) return null;
    host = host.slice(0, colon);
  }
  if (host.startsWith('www.')) host = host.slice(4);
  return host.length <= 253 && host.split('.').every((label) => LABEL.test(label)) ? host : null;
};

/**
 * The address's host choice: `hosts=none` wins, then the repeated `host` keys (normalized, without repeats, at most
 * 50), and none of them means the default. Keys that are all malformed are an empty choice, never the default.
 */
export const parseHostParam = (params: URLSearchParams): HostSelection => {
  if (params.getAll('hosts').includes('none')) return { kind: 'none' };
  if (!params.has('host')) return params.has('hosts') ? { kind: 'none' } : { kind: 'default' };
  const hosts = [
    ...new Set(
      params
        .getAll('host')
        .map(normalizeHost)
        .filter((h): h is string => h !== null),
    ),
  ].slice(0, MAX_HOSTS);
  return hosts.length ? { kind: 'some', hosts } : { kind: 'none' };
};

/**
 * A saved choice kept to the hosts the brand still has: in the hosts GET, and not dropped by the latest answer's rows.
 * Nothing is dropped while the GET is degraded; a choice with no surviving host becomes `none`, never the default.
 */
export const reconcileHosts = (
  saved: HostSelection,
  catalogue: AnalyticsHosts,
  rows: readonly QualityHostRow[] | null,
): HostSelection => {
  if (saved.kind !== 'some' || catalogue.degraded) return saved;
  const dropped = new Set(
    (rows ?? []).filter((r) => r.reason === 'claimed_elsewhere' || r.reason === 'purpose_conflict').map((r) => r.host),
  );
  const chosen = new Set(saved.hosts);
  const hosts = [
    ...new Set(
      catalogue.hosts
        .map((h) => normalizeHost(h.host))
        .filter((h): h is string => h !== null && chosen.has(h) && !dropped.has(h)),
    ),
  ];
  if (!hosts.length) return { kind: 'none' };
  return hosts.length === saved.hosts.length && hosts.every((h, i) => h === saved.hosts[i])
    ? saved
    : { kind: 'some', hosts };
};

export type HostGroup = { exposure: QualityExposure; hosts: QualityHostRow[] };

/** The kept hosts by exposure (public, internal, unclassified), in answer order; a group with no host is left out. */
export const hostGroups = (rows: readonly QualityHostRow[]): HostGroup[] =>
  (['public', 'internal', 'unclassified'] as const)
    .map((exposure) => ({
      exposure,
      hosts: rows.filter((r) => r.exposure === exposure && (r.reason === null || r.reason === 'not_in_registry')),
    }))
    .filter((g) => g.hosts.length > 0);

/** The words a host row's kind reads as; an unclassified host is a landing host when its name starts with `lp.`. */
export const hostKindLabel = (row: { host: string; kind: QualityHostKind }): 'Website' | 'Landing pages' =>
  row.kind === 'landing' || (row.kind === 'unknown' && row.host.startsWith('lp.')) ? 'Landing pages' : 'Website';
